import React from 'react';
import { Coffee, Heart, ExternalLink, QrCode } from 'lucide-react';
import { t, Language } from '../../i18n';

interface SponsorshipViewProps {
    lang: Language;
}

export const SponsorshipView: React.FC<SponsorshipViewProps> = ({ lang }) => {
    const buyMeACoffeeUrl = "https://www.buymeacoffee.com/xleven"; // 예시 URL

    return (
        <div className="p-6 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-40">
            {/* Hero Section */}
            <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-rose-500 to-orange-500 p-8 text-white shadow-2xl shadow-rose-200">
                <div className="relative z-10">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6">
                        <Heart size={32} className="text-white fill-white animate-pulse" />
                    </div>
                    <h2 className="text-3xl font-black leading-tight mb-4 tracking-tight">
                        {t(lang, 'sponsorshipTitle')}
                    </h2>
                    <p className="text-rose-50 font-medium leading-relaxed opacity-90">
                        {t(lang, 'sponsorshipDesc')}
                    </p>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full -ml-16 -mb-16 blur-2xl" />
            </div>

            {/* Support Options */}
            <div className="grid gap-4">
                <a 
                    href={buyMeACoffeeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative overflow-hidden flex items-center justify-between p-6 rounded-[2rem] border-2 border-slate-100 bg-white hover:border-rose-500 hover:shadow-xl hover:shadow-rose-100 transition-all duration-300 active:scale-[0.98]"
                >
                    <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-white transition-colors duration-300">
                            <Coffee size={28} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xl font-black text-slate-900 tracking-tight">Buy Me a Coffee</span>
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                {t(lang, 'supportSmallCoffee')}
                            </span>
                        </div>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-rose-50 group-hover:text-rose-500 transition-all">
                        <ExternalLink size={18} />
                    </div>
                </a>

                <div className="rounded-[2rem] border-2 border-slate-50 bg-slate-50/50 flex flex-col items-center text-center">
                    <div className="w-48 h-48 bg-white p-4 rounded-3xl shadow-sm mb-6 border border-slate-100 flex items-center justify-center">
                        <QrCode size={120} className="text-slate-900" />
                    </div>
                    <h4 className="text-lg font-black text-slate-900 mb-2">{t(lang, 'qrDonation')}</h4>
                    <p className="text-sm text-slate-400 font-medium leading-relaxed md:max-w-[240px]">
                        {t(lang, 'qrDonationDesc')}
                    </p>
                </div>
            </div>

            {/* Note Section */}
            <div className="p-6 rounded-[2rem] bg-white border-2 border-slate-100">
                <h5 className="text-sm font-black text-slate-900 mb-3 uppercase tracking-wider">{t(lang, 'sponsorshipWhy')}</h5>
                <p className="text-sm text-slate-500 leading-relaxed font-medium">
                    {t(lang, 'sponsorshipWhyDesc')}
                </p>
            </div>

        </div>
    );
};
